import { BirthDatePicker, FormContainer, LabeledInput, Worksheet } from "uikit"
import React from "react"
import { Dropdown, Grid } from "semantic-ui-react"
import { Footer } from "./Footer"
import { useStore } from "store"

const gridHeaderStyle = {
  borderBottom: "2px solid #dedede",
  padding: "1em 0em 1em 1.2em",
  fontSize: "1.5em",
  fontWeight: "700"
}

const PersonForm = ({
  data: {
    fields: {
      first_name,
      last_name,
      birth_date,
      gender,
      health_status,
      is_tobacco_user,
      medical_expenses,
      credit_rating
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  role,
  submitLabel = "Done",
  submitHeader = "",
  onCancel,
  onDelete,
  baseUrl,
  canAddOwner,
  household,
  isNew,
  genderOptions,
  healthStatusOptions,
  tobaccoUsageOptions,
  creditRatingOptions,
  verification
}) => {
  first_name.role = role || "child"
  return (
    <Worksheet error={error}>
      <Grid.Row className="white" style={gridHeaderStyle}>
        <Grid.Column>{`${isNew ? "Add" : "Edit"} Person`}</Grid.Column>
      </Grid.Row>
      <Worksheet.Body>
        <Worksheet.Row label="First Name" paddedField>
          <Worksheet.Field error={first_name.error}>
            <input {...first_name} />
          </Worksheet.Field>
        </Worksheet.Row>
        {role !== "child" ? (
          <Worksheet.Row label="Last Name" paddedField>
            <Worksheet.Field error={last_name.error}>
              <input {...last_name} />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
        <Worksheet.Row label="Birth Date" paddedField>
          <Worksheet.Field error={birth_date.error}>
            <BirthDatePicker {...birth_date} />
          </Worksheet.Field>
        </Worksheet.Row>
        {role !== "child" ? (
          <Worksheet.Row label="Gender" paddedField>
            <Worksheet.Field error={gender.error}>
              <Dropdown
                {...gender}
                fluid
                search
                selection
                options={genderOptions}
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
        {role !== "child" ? (
          <Worksheet.Row label="Health Status" paddedField>
            <Worksheet.Field error={health_status.error}>
              <Dropdown
                {...health_status}
                fluid
                search
                selection
                options={healthStatusOptions}
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
        {role !== "child" ? (
          <Worksheet.Row label="Tobacco Usage" paddedField>
            <Worksheet.Field error={is_tobacco_user.error}>
              <Dropdown
                {...is_tobacco_user}
                fluid
                search
                selection
                options={tobaccoUsageOptions}
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
        <Worksheet.Row
          label="Medical Expenses"
          description={`Typical yearly qualified medical expenses for ${first_name.value}. Qualified medical expenses must be primarily to alleviate or prevent a physical or mental disability or illness. They don't include expenses that are merely beneficial to general health, such as vitamins or a vacation. See IRS Publication 502 for more information.`}
          paddedField
        >
          <Worksheet.Field error={medical_expenses.error}>
            <LabeledInput
              {...medical_expenses}
              label=""
              iconText="$"
              iconPosition="left"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
        {role !== "child" ? (
          <Worksheet.Row label="Credit Rating" paddedField>
            <Worksheet.Field error={credit_rating.error}>
              <Dropdown
                {...credit_rating}
                fluid
                search
                selection
                options={creditRatingOptions}
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
        <Footer
          baseUrl={baseUrl}
          submitLabel="Done"
          isNew={isNew}
          onSubmit={onSubmit}
          onCancel={onCancel}
          isPending={isPending}
          onDelete={onDelete}
          isModified={isModified}
          entityName="person"
          deleteLabel="Delete"
          verification={verification}
          showDeleteButton={role !== "client"}
          error={error}
        />
      </Worksheet.Body>
    </Worksheet>
  )
}

const PersonWorksheetFormContainer = FormContainer(
  {
    first_name: {
      type: "string"
    },
    last_name: {
      type: "string",
      required: false
    },
    birth_date: {
      type: "birthdate",
      required: true
    },
    gender: {
      type: "string",
      required: false
    },
    health_status: {
      type: "string",
      required: false
    },
    is_tobacco_user: {
      type: "boolean",
      required: false
    },
    medical_expenses: {
      type: "unit",
      required: false
    },
    credit_rating: {
      type: "string",
      required: false
    }
  },
  ({ fields }) => {
    const { birth_date, first_name } = fields
    if (first_name.role && first_name.role !== "child") {
      const [month, day, year] = birth_date.value.split("/")
      const parsedDate = new Date(year, month - 1, day)
      if (parsedDate > new Date()) {
        return {
          fields: {
            birth_date: "Birth dates in the future are not allowed."
          }
        }
      }
    }
  }
)(PersonForm)

const PersonWorksheet = ({
  household,
  newPerson,
  personId,
  role,
  ...formProps
}) => {
  const [person, { updatePerson, createPerson, deletePerson }] = useStore(
    "people",
    personId
  )

  const isNew = newPerson === null
  const values = isNew ? { role } : { ...person }
  const verification = isNew
    ? {}
    : {
        prompt: (
          <span>
            You are about to delete <strong>{values.first_name}</strong> from
            the household. Please confirm by entering{" "}
            <strong>DELETE {values.first_name.toUpperCase()}</strong> in the
            textbox below.
          </span>
        ),
        text: `DELETE ${values.first_name.toUpperCase()}`
      }

  const genderOptions = [
    { text: "Male", value: "male" },
    { text: "Female", value: "female" }
  ]
  const healthStatusOptions = [
    { text: "Excellent", value: "excellent" },
    { text: "Good", value: "good" },
    { text: "Fair", value: "fair" },
    { text: "Poor", value: "poor" }
  ]
  const tobaccoUsageOptions = [
    { text: "Yes", value: "true" },
    { text: "No", value: "false" }
  ]
  const creditRatingOptions = [
    { text: "Excellent", value: "excellent" },
    { text: "Good", value: "good" },
    { text: "Fair", value: "fair" }
  ]

  const submit = isNew
    ? async values => {
        await createPerson({
          household_id: household.id,
          role,
          ...values
        })
      }
    : async values => {
        await updatePerson({ id: person.id, role, ...values })
      }

  const onDelete = async () => {
    await deletePerson({ household_id: household.id, id: person.id })
    window.location.reload()
  }

  return (
    <PersonWorksheetFormContainer
      {...{
        submit,
        onDelete,
        values,
        genderOptions,
        healthStatusOptions,
        tobaccoUsageOptions,
        creditRatingOptions,
        verification,
        isNew,
        role: values.role,
        ...formProps
      }}
    />
  )
}

export default PersonWorksheet
